'use client';
import React, { useState, useEffect } from 'react';
import { Translations } from '@/lib/i18n';

interface VideoData {
    id: number;
    video_url: string;
    title: string;
    is_active: number;
}

interface Props {
    t: Translations;
    language: string;
}

export default function VideoSection({ t, language }: Props) {
    const [video, setVideo] = useState<VideoData | null>(null);

    useEffect(() => {
        fetchVideo();
    }, [language]);

    const fetchVideo = async () => {
        try {
            const baseUrl = window.location.origin;
            const response = await fetch(`${baseUrl}/api/index.php?route=video&lang=${language}`);
            const data = await response.json();

            if (data.success && data.data) {
                setVideo(data.data);
            }
        } catch (error) {
            console.error('Video fetch error:', error);
        }
    };

    const getEmbedUrl = (url: string): string => {
        // YouTube
        const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
        if (ytMatch) {
            return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&mute=1`;
        }

        // Vimeo
        const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
        if (vimeoMatch) {
            return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
        }

        return url;
    };

    const isEmbeddable = (url: string): boolean => {
        return (
            url.includes('youtube.com') ||
            url.includes('youtu.be') ||
            url.includes('vimeo.com')
        );
    };

    if (!video || !video.is_active) {
        return null;
    }

    return (
        <section data-component-id="src\components\VideoSection.tsx:67:8" data-component-path="src\components\VideoSection.tsx" data-component-line="67" data-component-end-line="120" data-component-file="VideoSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-12%20px-4%22%7D"
            className="py-12 px-4"
            style={{ backgroundColor: 'rgb(217,217,217)' }}
            dir={t.dir}
        >
            <div data-component-id="src\components\VideoSection.tsx:72:12" data-component-path="src\components\VideoSection.tsx" data-component-line="72" data-component-end-line="119" data-component-file="VideoSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-4xl%20mx-auto%22%7D" className="max-w-4xl mx-auto">
                {video.title && (
                    <h2 data-component-id="src\components\VideoSection.tsx:74:20" data-component-path="src\components\VideoSection.tsx" data-component-line="74" data-component-end-line="76" data-component-file="VideoSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-2xl%20font-bold%20text-center%20text-gray-800%20mb-6%22%7D" className="text-2xl font-bold text-center text-gray-800 mb-6">
                        {video.title}
                    </h2>
                )}

                <div data-component-id="src\components\VideoSection.tsx:79:16" data-component-path="src\components\VideoSection.tsx" data-component-line="79" data-component-end-line="108" data-component-file="VideoSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-white%20rounded-2xl%20shadow-xl%20overflow-hidden%22%7D" className="bg-white rounded-2xl shadow-xl overflow-hidden">
                    <div data-component-id="src\components\VideoSection.tsx:80:20" data-component-path="src\components\VideoSection.tsx" data-component-line="80" data-component-end-line="107" data-component-file="VideoSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22aspect-video%20bg-gray-900%22%7D" className="aspect-video bg-gray-900">
                        {isEmbeddable(video.video_url) ? (
                            <iframe data-component-id="src\components\VideoSection.tsx:82:28" data-component-path="src\components\VideoSection.tsx" data-component-line="82" data-component-end-line="88" data-component-file="VideoSection.tsx" data-component-name="iframe" data-component-content="%7B%22elementName%22%3A%22iframe%22%2C%22className%22%3A%22w-full%20h-full%22%7D"
                                src={getEmbedUrl(video.video_url)}
                                className="w-full h-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                title="Hoca Tanıtım Videosu"
                            />
                        ) : (
                            <video data-component-id="src\components\VideoSection.tsx:90:28" data-component-path="src\components\VideoSection.tsx" data-component-line="90" data-component-end-line="105" data-component-file="VideoSection.tsx" data-component-name="video" data-component-content="%7B%22elementName%22%3A%22video%22%2C%22className%22%3A%22w-full%20h-full%22%7D"
                                className="w-full h-full"
                                controls
                                autoPlay
                                muted
                                loop
                            >
                                <source data-component-id="src\components\VideoSection.tsx:97:32" data-component-path="src\components\VideoSection.tsx" data-component-line="97" data-component-end-line="97" data-component-file="VideoSection.tsx" data-component-name="source" data-component-content="%7B%22elementName%22%3A%22source%22%2C%22type%22%3A%22video%2Fmp4%22%7D" src={video.video_url.startsWith('http') || video.video_url.startsWith('/') ? video.video_url : `/${video.video_url}`} type="video/mp4" />
                                {language === 'tr'
                                    ? 'Tarayıcınız video etiketini desteklemiyor.'
                                    : language === 'ar'
                                        ? 'متصفحك لا يدعم تشغيل الفيديو.'
                                        : language === 'en'
                                            ? 'Your browser does not support the video tag.'
                                            : 'Ihr Browser unterstützt das Video-Tag nicht.'}
                            </video>
                        )}
                    </div>
                </div>

                <p data-component-id="src\components\VideoSection.tsx:110:16" data-component-path="src\components\VideoSection.tsx" data-component-line="110" data-component-end-line="118" data-component-file="VideoSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-center%20text-sm%20text-gray-500%20mt-4%22%7D" className="text-center text-sm text-gray-500 mt-4">
                    {language === 'tr'
                        ? 'Videoyu görmek için tarayıcınızda otomatik oynatma izni vermeniz gerekebilir.'
                        : language === 'ar'
                            ? 'قد تحتاج إلى السماح بالتشغيل التلقائي في متصفحك لمشاهدة الفيديو.'
                            : language === 'en'
                                ? 'You may need to allow autoplay in your browser to view the video.'
                                : 'Möglicherweise müssen Sie die automatische Wiedergabe in Ihrem Browser erlauben.'}
                </p>
            </div>
        </section>
    );
}
